<template>
  <d-container fluid class="main-content-container px-4 product-form">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Stany materiałów</span>
        <h3 class="page-title">
          Edycja stanów materiałów produktu
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/stany-materialow/produkty')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs card>
          <!-- Dane ogólne -->
          <d-tab title="Dane ogólne" active>
            <h4 class="mb-3 pb-3 border-bottom">
              Dane ogólne
            </h4>

            <div v-if="allDataLoaded">
              <div class="form-group">
                <label for="name">
                  Nazwa produktu:
                </label>

                <strong class="text-primary">
                  {{ data.name }}
                </strong>
              </div>

              <div class="form-group">
                <label for="code">
                  Kod produktu:
                </label>

                <strong class="text-primary">
                  {{ data.code }}
                </strong>
              </div>

              <div class="form-group">
                <label for="price_code">
                  Kod ceny:
                </label>

                <strong class="text-primary">
                  {{ data.price_code }}
                </strong>
              </div>

              <div class="form-group">
                <h5 class="mb-3">
                  Użycie materiałów:
                </h5>

                <validation-observer
                  ref="usage-form"
                  tag="form">
                  <table class="table">
                    <thead>
                      <tr>
                        <th>Materiał</th>
                        <th>Rozmiar</th>
                        <th>Użycie w cm2</th>
                        <th>Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!data.materials_usages.length">
                        <td colspan="3">
                          <d-alert
                            theme="light"
                            class="text-center">
                            Brak informacji o użyciu materiałów dla tego produktu...
                          </d-alert>
                        </td>
                      </tr>
                      <tr
                        v-for="(material_usage, index) of data.materials_usages"
                        :key="'material-usage-' + index">
                        <td>
                          <validation-provider
                            rules="required"
                            :name="'material-usage-id-' + index"
                            v-slot="{ errors }">
                            <d-form-select
                              v-model="material_usage.material_id"
                              :state="errors.length ? 'invalid' : null">
                              <option :value="null">Wybierz materiał</option>
                              <option
                                v-for="(material, index) of productMaterials"
                                :key="'material-usage-option-' + index"
                                :value="material.value">
                                {{ material.text }}
                              </option>
                            </d-form-select>
                            <div class="invalid-feedback">
                              {{ errors[0] }}
                            </div>
                          </validation-provider>
                        </td>
                        <td>
                          <validation-provider
                            rules="required"
                            :name="'material-usage-sizes-id-' + index"
                            v-slot="{ errors }">
                            <d-form-select
                              v-model="material_usage.size_id"
                              :state="errors.length ? 'invalid' : null">
                              <option :value="null">Wybierz rozmiar</option>
                              <option
                                v-for="(size, index) of productSizes"
                                :key="'material-usage-size-option-' + index"
                                :value="size.value">
                                {{ size.text }}
                              </option>
                            </d-form-select>
                            <div class="invalid-feedback">
                              {{ errors[0] }}
                            </div>
                          </validation-provider>
                        </td>
                        <td style="width: 200px;">
                          <validation-provider
                            rules="required"
                            name="name"
                            v-slot="{ errors }">
                            <d-input-group
                              append="cm2"
                              :state="errors.length ? 'invalid' : null">
                              <d-input
                                type="number"
                                class="text-right"
                                :min="0"
                                :step="0.01"
                                :state="errors.length ? 'invalid' : null"
                                v-model="material_usage.usage" />
                            </d-input-group>
                            <div class="invalid-feedback">
                              {{ errors[0] }}
                            </div>
                          </validation-provider>
                        </td>
                        <td style="width: 100px;">
                          <d-button
                            theme="danger"
                            @click.prevent="removeUsage(index)"
                            title="Usuń użycie">
                            <i class="material-icons">delete</i>
                          </d-button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </validation-observer>

                <div class="text-center">
                  <d-button
                    theme="success"
                    :outline="true"
                    class="w-50 ml-auto mr-auto"
                    @click.prevent="addUsage">
                    Dodaj użycie materiału
                  </d-button>
                </div>
              </div>
            </div>

            <div
              v-if="!allDataLoaded"
              class="pb-3 pt-3 text-center">
              Trwa ładowanie danych&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
            </d-alert>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'materials-stock-product-form',
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedMaterials;
    },
    productMaterials () {
      return this.materials.map(material => ({
        text: material.name,
        value: material.id
      }));
    },
    productSizes () {
      return this.sizes.map(size => ({
        text: size.name,
        value: size.id
      }));
    }
  },
  data() {
    return {
      data: {
        id: '',
        name: '',
        code: '',
        price_code: '',
        materials_usages: []
      },
      loaded: false,
      loadedMaterials: false,
      loadedSizes: false,
      loadError: false,
      materials: [],
      sizes: []
    };
  },
  mounted () {
    this.loadItemData();
    this.loadAdditionalData();
  },
  methods: {
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/materials-stock/products/item/',
        instanceFields: ['id', 'name', 'code', 'price_code', 'materials_usages'],
        loadedKey: 'loaded',
        successAction: () => {
          if (this.data.sizes) {
            this.data.sizes = this.data.sizes.map(size => ({
              text: size.name,
              value: size.id
            }));
          }

          if (this.data.procedures) {
            this.data.procedures = this.data.procedures.map(procedure => ({
              text: procedure.name,
              value: procedure.id
            }));
          }
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/sizes/items',
        outputKey: 'sizes',
        loadedKey: 'loadedSizes',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/materials/items',
        outputKey: 'materials',
        loadedKey: 'loadedMaterials',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['usage-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();

      FormUtils.submit(this, {
        endpoint: '/materials-stock/products/update/' + parseInt(this.data.id, 10),
        formData: formDataToSend,
        successTitle: 'Zapisano stan materiałów',
        successText: 'Stan materiałów w wybranym produkcie zostął zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja stanów materiałów w produkcie nie powiodła się. Spróbuj ponownie...',
        successAction: () => {
          this.loaded = false;
          this.loadItemData();
        }
      });
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        materials_usage: []
      };

      for (let i = 0; i < this.data.materials_usages.length; i++) {
        formDataToSend.materials_usage.push({
          material_id: this.data.materials_usages[i].material_id,
          size_id: this.data.materials_usages[i].size_id,
          usage: parseFloat(this.data.materials_usages[i].usage)
        });
      }

      return formDataToSend;
    },
    addUsage () {
      this.data.materials_usages.push({
        'material_id': null,
        'size_id': null,
        'usage': 0.00
      });
    },
    removeUsage (index) {
      this.data.materials_usages.splice(index, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
.product-form {
  /deep/ .v-select {
    .vs__dropdown-toggle {
      border: 1px solid #e1e5eb;

      .vs__selected-options {
        .vs__selected {
          margin-right: 3px!important;
        }
      }
    }
  }
}
</style>
